import React, { useState } from 'react';
import { List, useTable, DeleteButton } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { Table, Space, Tag, Input, Select, Button, Tooltip, Popconfirm } from 'antd';
import { SearchOutlined, LinkOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../providers/dataProvider';

const ENTITY_TYPE_COLORS: Record<string, string> = {
  DISH: '#3ecf8e',
  FOOD_LABEL: '#1890ff',
  EQUIPMENT: '#f5a623',
  SPORT_ACTIVITY: '#722ed1',
  FOOD_BARCODE: '#eb2f96',
};

export const VisualEntityList: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const invalidate = useInvalidate();

  const { tableProps } = useTable({
    resource: 'visual-entities',
    filters: {
      permanent: [
        { field: 'search', operator: 'contains' as const, value: searchText },
        ...(selectedType ? [{ field: 'entityType', operator: 'eq' as const, value: selectedType }] : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  const bulkDelete = async () => {
    if (!selectedRowKeys.length) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('access_token');
      await fetch(`${API_BASE_URL}/vision-admin/entities/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: selectedRowKeys }),
      });
      setSelectedRowKeys([]);
      invalidate({ resource: 'visual-entities', invalidates: ['list'] });
    } finally {
      setDeleting(false);
    }
  };

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <List resource="visual-entities">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by name..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
        <Select
          placeholder="Filter by type"
          allowClear
          style={{ width: 200 }}
          value={selectedType}
          onChange={(value) => setSelectedType(value)}
        >
          {Object.keys(ENTITY_TYPE_COLORS).map((t) => (
            <Select.Option key={t} value={t}>{t}</Select.Option>
          ))}
        </Select>
        {hasSelected && (
          <Popconfirm
            title={`Delete ${selectedRowKeys.length} entities permanently?`}
            okText="Delete all"
            okButtonProps={{ danger: true }}
            onConfirm={bulkDelete}
          >
            <Button danger icon={<DeleteOutlined />} loading={deleting}>
              Delete ({selectedRowKeys.length})
            </Button>
          </Popconfirm>
        )}
      </div>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 1500 }}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
      >
        <Table.Column
          title="Image"
          width={72}
          render={(_: any, record: any) => {
            const url = (record.metadata as any)?._imageUrl;
            return url ? (
              <img
                src={url}
                alt={record.name}
                style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: '1px solid #f0f0f0' }}
                onClick={() => navigate(`/visual-entities/show/${record.id}`)}
              />
            ) : (
              <div style={{ width: 48, height: 48, borderRadius: 6, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d9d9d9', fontSize: 20, border: '1px solid #f0f0f0' }}>
                📷
              </div>
            );
          }}
        />
        <Table.Column
          dataIndex="name"
          title="Name"
          fixed="left"
          width={220}
          render={(name: string, record: any) => (
            <a onClick={() => navigate(`/visual-entities/show/${record.id}`)} style={{ fontWeight: 500 }}>
              {name}
            </a>
          )}
        />
        <Table.Column
          dataIndex="entityType"
          title="Type"
          width={130}
          render={(type: string) => {
            const color = ENTITY_TYPE_COLORS[type] || '#898989';
            return (
              <Tag style={{ borderColor: color, color, background: `${color}15`, borderRadius: 9999, padding: '2px 12px', fontWeight: 500 }}>
                {type}
              </Tag>
            );
          }}
        />
        <Table.Column
          title="Owner"
          width={120}
          render={(_: any, record: any) => (
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {record.ownerId ? `${record.ownerId.slice(0, 8)}…` : '—'}
            </span>
          )}
        />
        <Table.Column
          dataIndex="sourceProvider"
          title="Source"
          width={100}
          render={(v: string) => v || '—'}
        />
        <Table.Column
          dataIndex="confidence"
          title="Confidence"
          width={100}
          render={(v: number) => v != null ? `${(v * 100).toFixed(0)}%` : '—'}
        />
        <Table.Column
          title="Translations"
          width={200}
          render={(_: any, record: any) => {
            const t = record.translations;
            if (!t || Object.keys(t).length === 0) return '—';
            return (
              <Space wrap size={4}>
                {Object.keys(t).map((locale) => (
                  <Tag key={locale} style={{ borderRadius: 9999, padding: '2px 8px', fontSize: 12 }}>
                    {locale}: {t[locale].name}
                  </Tag>
                ))}
              </Space>
            );
          }}
        />
        <Table.Column
          title="Components"
          width={90}
          render={(_: any, record: any) => record.componentCount ?? 0}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created"
          width={160}
          render={(v: string) => new Date(v).toLocaleString()}
        />
        <Table.Column
          title="Actions"
          fixed="right"
          width={130}
          render={(_: any, record: any) => (
            <Space>
              <Tooltip title="View detail">
                <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/visual-entities/show/${record.id}`)} />
              </Tooltip>
              {record.llmCallLogId && (
                <Tooltip title="View LLM Call Log">
                  <a href={`/llm-call-logs/show/${record.llmCallLogId}`}>
                    <Button type="text" size="small" icon={<LinkOutlined />} />
                  </a>
                </Tooltip>
              )}
              <DeleteButton
                hideText
                size="small"
                recordItemId={record.id}
                resource="visual-entities"
                confirmTitle="Permanently delete this visual entity?"
                confirmOkText="Delete permanently"
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
